import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FileAudio, Loader2, Pause, Play, RotateCcw, Trash2, Upload } from 'lucide-react';
import { TableCard } from '../../TableCard';
import { ConfirmDialog } from '../../ConfirmDialog';
import { formatShortWhen, parseUtc } from '../../formatWhen';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useStableText } from '../../../../components/StableText';
import { TranscriptView } from './TranscriptView';
import { TranscriberKey } from './TranscriberKey';
import { AudioPlayer } from './AudioPlayer';
import { apiUrl } from '../../../../apiBase';
import {
  clock, uploadTranscript,
  type Transcript, type TranscriptStatus, type TranscriberKeyInfo, type Worker,
} from './transcriptsApi';

// MEETINGS, TRANSCRIBED WITH WHO SAID WHAT. Somebody uploads a recording here; a worker on
// another machine (transcriber/, WhisperX + pyannote) takes it off the queue and sends back
// the turns. The server never transcribes — see notice_transcripts.py for why — so this tab
// is a queue seen from the browser, and the first thing it says is whether anything is on
// the other end of it.

// The worker asks every 30 s, so three missed calls is a machine that is off or asleep.
const WORKER_FRESH_MS = 90_000;
// How often the list refreshes itself, to watch a meeting move along the queue — and its
// percentage, which the worker reports every 5 s.
const POLL_MS = 5_000;

const STATUS_CLASS: Record<TranscriptStatus, string> = {
  uploading: 'bg-slate-100 text-slate-600',
  queued: 'bg-amber-100 text-amber-700',
  running: 'bg-blue-100 text-blue-700',
  done: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
};

const INPUT = 'w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-800 focus:border-blue-500 focus:outline-none';
const LABEL = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500';

// The machine and the last call are in the tooltip: the line itself only has to say whether
// anything will pick a meeting up.
function WorkerLine({ worker }: { worker: Worker }) {
  const { t, language } = useLanguage();
  const seen = worker?.seenAt ? parseUtc(worker.seenAt) : null;
  const on = !!seen && Date.now() - seen.getTime() < WORKER_FRESH_MS;
  const detail = worker?.seenAt
    ? `${worker.host ?? ''} · ${formatShortWhen(worker.seenAt, language)}`
    : undefined;
  return (
    <span className="inline-flex items-center gap-2" title={detail}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${on ? 'bg-emerald-500' : 'bg-slate-300'}`} />
      {t(on ? 'notice.transcripts.workerOn' : seen ? 'notice.transcripts.workerOff' : 'notice.transcripts.workerNever')}
    </span>
  );
}

function UploadCard({ onUploaded }: { onUploaded: () => void }) {
  const { t } = useLanguage();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [lang, setLang] = useState('fr');
  const [speakers, setSpeakers] = useState('');
  const [prompt, setPrompt] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = (f: File | undefined) => {
    if (!f) return;
    setFile(f);
    setTitle(f.name.replace(/\.[^.]+$/, ''));
    setError(null);
  };

  const submit = async () => {
    if (!file) return;
    setError(null);
    setProgress(0);
    try {
      await uploadTranscript(
        { file, title, language: lang, speakers: Number(speakers) || null, prompt },
        setProgress,
      );
      setFile(null);
      setTitle('');
      setSpeakers('');
      setPrompt('');
      if (fileInput.current) fileInput.current.value = '';
      onUploaded();
    } catch (e) {
      const msg = axios.isAxiosError(e) ? e.response?.data?.error : undefined;
      setError(msg || t('notice.transcripts.uploadError'));
    } finally {
      setProgress(null);
    }
  };

  const busy = progress !== null;
  return (
    <TableCard title={t('notice.transcripts.new')} className="flex flex-col gap-3">
      <button
        type="button"
        disabled={busy}
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
        className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500 transition-colors hover:border-blue-400 hover:bg-blue-50/40"
      >
        <FileAudio className="h-4 w-4 shrink-0 text-slate-400" />
        {file
          ? <span className="font-medium text-slate-800">{file.name} · {(file.size / 1e6).toFixed(1)} MB</span>
          : t('notice.transcripts.pick')}
      </button>
      <input
        ref={fileInput}
        type="file"
        // NO `accept`: a recording off Google Drive can have no extension at all (an mp3
        // called "Pierre Sire (14:10:26)"), and the picker would grey it out. ffmpeg, on the
        // worker, reads the bytes rather than the name.
        className="hidden"
        onChange={(e) => pick(e.target.files?.[0])}
      />

      {file && (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className={LABEL}>{t('notice.transcripts.titleField')}</span>
            <input className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label>
            <span className={LABEL}>{t('notice.transcripts.language')}</span>
            <select className={INPUT} value={lang} onChange={(e) => setLang(e.target.value)}>
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="">{t('notice.transcripts.languageAuto')}</option>
            </select>
          </label>
          <label>
            <span className={LABEL}>{t('notice.transcripts.speakers')}</span>
            <input
              className={INPUT}
              type="number"
              min={1}
              max={20}
              placeholder={t('notice.transcripts.speakersUnknown')}
              value={speakers}
              onChange={(e) => setSpeakers(e.target.value)}
            />
          </label>
          <label className="sm:col-span-2">
            <span className={LABEL}>{t('notice.transcripts.prompt')}</span>
            <input
              className={INPUT}
              placeholder={t('notice.transcripts.promptHint')}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </label>
        </div>
      )}

      {error && <p className="text-center text-sm text-rose-600">{error}</p>}

      {file && (
        <div className="flex flex-col items-center gap-2">
          {busy && (
            <div className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-slate-200">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.round((progress ?? 0) * 100)}%` }} />
            </div>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {busy ? `${Math.round((progress ?? 0) * 100)} %` : t('notice.transcripts.upload')}
          </button>
        </div>
      )}
    </TableCard>
  );
}

export function TranscriptsSection() {
  const { t, language } = useLanguage();
  const st = useStableText();
  const [items, setItems] = useState<Transcript[]>([]);
  const [worker, setWorker] = useState<Worker>(null);
  const [keyInfo, setKeyInfo] = useState<TranscriberKeyInfo | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  // The recording playing in the bar under the list, if any. Any meeting whose file is
  // whole can be heard, transcribed or not: listening is how somebody finds the one to send.
  const [playingId, setPlayingId] = useState<number | null>(null);
  const playing = items.find((item) => item.id === playingId) ?? null;
  const [deleting, setDeleting] = useState<Transcript | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get<{
        transcripts: Transcript[]; worker: Worker; key: TranscriberKeyInfo | null;
      }>(
        '/api/notice/transcripts',
      );
      setItems(data.transcripts);
      setWorker(data.worker);
      setKeyInfo(data.key);
      setError(null);
    } catch {
      setError(t('notice.transcripts.loadError'));
    } finally {
      setLoaded(true);
    }
  }, [t]);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const requeue = async (item: Transcript) => {
    try {
      await axios.post(`/api/notice/transcripts/${item.id}/requeue`);
    } catch {
      setError(t('notice.transcripts.requeueError'));
    }
    await load();
  };

  const remove = async (item: Transcript) => {
    setDeleting(null);
    try {
      await axios.delete(`/api/notice/transcripts/${item.id}`);
    } catch {
      setError(t('notice.transcripts.deleteError'));
    }
    await load();
  };

  if (openId !== null) {
    return <TranscriptView id={openId} onBack={() => { setOpenId(null); void load(); }} />;
  }

  return (
    <div className="flex flex-col gap-10">

      <TableCard title={t('notice.transcripts.title')} className="flex flex-col gap-4">
        {error && <p className="text-center text-sm text-rose-600">{error}</p>}
        {!loaded ? (
          <div className="flex justify-center py-8 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-slate-400">{t('notice.transcripts.empty')}</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[34rem] table-fixed text-sm">
              <thead className="bg-slate-100 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-[8%] px-2 py-2.5" />
                  <th className="w-[32%] px-4 py-2.5">{st('notice.transcripts.meeting')}</th>
                  <th className="w-[20%] px-4 py-2.5">{st('notice.transcripts.added')}</th>
                  <th className="w-[12%] px-4 py-2.5">{st('notice.transcripts.length')}</th>
                  <th className="w-[16%] px-4 py-2.5">{st('notice.transcripts.status')}</th>
                  <th className="w-[12%] px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item) => (
                  <tr key={item.id} className={item.id === playingId ? 'bg-blue-50/60' : 'bg-white'}>
                    <td className="px-2 py-3">
                      {item.status !== 'uploading' && (
                        <button
                          type="button"
                          onClick={() => setPlayingId(item.id === playingId ? null : item.id)}
                          title={t(item.id === playingId ? 'notice.transcripts.stop' : 'notice.transcripts.listen')}
                          aria-label={t(item.id === playingId ? 'notice.transcripts.stop' : 'notice.transcripts.listen')}
                          className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-500"
                        >
                          {item.id === playingId
                            ? <Pause className="h-4 w-4" fill="currentColor" />
                            : <Play className="ml-0.5 h-4 w-4" fill="currentColor" />}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.status === 'done' ? (
                        <button
                          type="button"
                          onClick={() => setOpenId(item.id)}
                          className="truncate font-medium text-blue-700 hover:underline"
                        >
                          {item.title}
                        </button>
                      ) : (
                        <span className="truncate font-medium text-slate-800">{item.title}</span>
                      )}
                      {item.status === 'failed' && item.error && (
                        <div className="truncate text-xs text-rose-600" title={item.error}>{item.error}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600">
                      {item.createdAt ? formatShortWhen(item.createdAt, language) : '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600">
                      {item.duration ? clock(item.duration) : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLASS[item.status]}`}>
                        {item.status === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
                        {t(`notice.transcripts.status.${item.status}`)}
                        {item.status === 'running' && item.progress !== null && (
                          <span className="tabular-nums">{Math.round(item.progress * 100)} %</span>
                        )}
                      </span>
                      {item.status === 'running' && item.progress !== null && (
                        <div className="mx-auto mt-1.5 h-1 w-20 overflow-hidden rounded-full bg-blue-100">
                          <div
                            className="h-full bg-blue-500 transition-all duration-700"
                            style={{ width: `${Math.round(item.progress * 100)}%` }}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* A failure, a run whose machine went to sleep, or a finished one
                            to redo with a better speaker count — the same button. */}
                        {(item.status === 'failed' || item.status === 'running' || item.status === 'done') && (
                          <button
                            type="button"
                            title={t('notice.transcripts.requeue')}
                            aria-label={t('notice.transcripts.requeue')}
                            onClick={() => void requeue(item)}
                            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          title={t('notice.transcripts.delete')}
                          aria-label={t('notice.transcripts.delete')}
                          onClick={() => setDeleting(item)}
                          className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* UNDER THE LIST, not in its row: one recording plays at a time, and the bar
            stays where it is while the list refreshes itself every 15 s. */}
        {playing && (
          <div className="flex flex-col gap-2 rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3">
            <p className="truncate text-center text-sm font-medium text-slate-800">{playing.title}</p>
            <AudioPlayer
              key={playing.id}
              src={apiUrl(`/api/notice/transcripts/${playing.id}/audio`)}
              autoPlay
            />
          </div>
        )}

        {/* AT THE FOOT, small: is a transcriber on, and the key it uses — set up once,
            looked at rarely. */}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-slate-400">
          <WorkerLine worker={worker} />
          {loaded && <TranscriberKey info={keyInfo} onChange={() => void load()} />}
        </div>
      </TableCard>

      {/* UNDER THE LIST: the meetings are what this tab is opened for, and adding one is
          the rarer thing — most arrive from the Drive folder without anybody here. */}
      <UploadCard onUploaded={() => void load()} />

      {deleting && (
        <ConfirmDialog
          title={t('notice.transcripts.deleteConfirm.title')}
          body={t('notice.transcripts.deleteConfirm.body').replace('{title}', deleting.title)}
          confirmLabel={t('notice.transcripts.delete')}
          cancelLabel={t('notice.catalog.cancel')}
          onConfirm={() => void remove(deleting)}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
